import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import classes from './GridMediaCard.module.scss';
import { DragSourceMonitor, useDrag, useDrop } from 'react-dnd';
import { Identifier } from 'dnd-core';
import classNames from 'classnames';
import { ReactComponent as LinkPreviewNoImage } from 'assets/images/linkPreviewNoImage.svg';
import { Icon } from '../../../../components/images/Icon/Icon';
import { IconsEnum } from '../../../../constants/icons';
import { PostQueuePosition, PostStatus, PostTiming } from '../../../../models/graphql';
import { getEmptyImage } from 'react-dnd-html5-backend';
import { ButtonMenu } from '../../../../components/actions/ButtonMenu';
import { ContainerListItem } from '../../../../components/actions/ListItem/ListItem.view';
import { PostEditorContextEnum } from '../../../../models/post-editor';
import { AppContext } from '../../../../providers/AppProvider';
import { debounce } from 'lodash';
import { Tooltip } from '../../../../components/actions/Tooltip';
import { useDateString } from '../../../../hooks/useDateString';
import { TDateTimeString } from '../../../../models/date';
import { DraggableTypesObject } from '../../../../constants/drag-and-drop';
import { Debugger } from '../../../../components/containers/Debugger';
import { InstagramGridPostType } from '../../../../models/post';
import { getFirstValidThumbnailUrl } from '../InstragramGridHelper';
import { PostPreviewModal } from '../../PostSchedule/PostPreviewModal';
import { ElementOverlay } from '../../../../components/containers/ElementOverlay';
import {
  ElementOverlayCursor,
  ElementOverlayPositions,
  ElementOverlaySize
} from '../../../../components/containers/ElementOverlay/ElementOverlay.view';
import { ContainerListItemExtended } from '../../../../components/actions/ButtonMenu/ButtonMenu.view';
import { ButtonColorsEnum } from '../../../../constants/button-colors';
import { useSubscriptionStatus } from 'hooks/useSubscriptionStatus';
import { OnboardingContext } from '../../../Onboarding/states/OnboardingProvider';
import { OnboardingStaticContent } from '../../../Onboarding/constants/content';

export type GridMediaCardViewProps = {
  index: number;
  isMobile: boolean;
  hasScrolling: boolean;
  isPlaceholder?: boolean;
  isGrabbed?: boolean;
  activeCategoryId?: string;
  post: InstagramGridPostType | undefined;
  movePost: (dragId: string, hoverIndex: number | undefined) => void;
  cancelMove: () => void;
  endMove: (post: InstagramGridPostType, refIndex: number) => void;
  movePostPosition: (postId: string, position: PostQueuePosition) => void;
  trashSinglePost: (postId: string) => void;
  reFetchPosts: (id: string) => void;
};

interface DragItem {
  index: number;
  post: InstagramGridPostType;
}

export const GridMediaCardView = (props: GridMediaCardViewProps): JSX.Element => {
  const ref = useRef<HTMLDivElement>(null);
  const { setPostEditorContext, setSuspendedModalOpen } = useContext(AppContext);
  const { formatDateString } = useDateString();
  const [stickyMenu, setStickyMenu] = useState<boolean>(false);
  const [showPostPreview, setShowPostPreview] = useState<boolean>(false);
  const [currentDragItem, setCurrentDragItem] = useState<DragItem | null>(null);
  const { showOnboarding, state } = useContext(OnboardingContext);
  const { version } = state;
  const disableDelete = OnboardingStaticContent[`v${version}`].disableDelete.posts;

  const {
    index,
    isMobile,
    hasScrolling,
    isPlaceholder,
    isGrabbed,
    activeCategoryId,
    post,
    movePost,
    cancelMove,
    endMove
  } = props;
  const hasVideo = post?.media.some(m => m.type === 'VIDEO');
  const isCarousel = post?.media && post.media.length > 1;

  const handleMovePost = useCallback(
    (item: DragItem) => {
      if (!ref.current) {
        return;
      }

      const dragIndex = item.index;
      const hoverIndex = index;

      // Don't replace items with themselves
      if (dragIndex === hoverIndex) {
        return;
      }

      if (!item.post) return;

      // Use itemId as index can change if we are fetching sub-queue
      const dragId = item.post.id;

      // Time to actually perform the action
      movePost(dragId, hoverIndex);

      item.index = hoverIndex;
    },
    [index, movePost]
  );

  const [{ handlerId }, drop] = useDrop<DragItem, void, { handlerId: Identifier | null }>({
    accept: DraggableTypesObject.IG_GRID_CARD,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId()
      };
    },
    hover(item: DragItem) {
      handleMovePost(item);
    }
  });

  const [{ isDragging }, drag, preview] = useDrag({
    type: isPlaceholder ? DraggableTypesObject.IG_GRID_PLACEHOLDER : DraggableTypesObject.IG_GRID_CARD,
    item: () => {
      if (!post) return { index, post: undefined }; // Return a consistent object shape

      const dragItem = { index, post };
      setCurrentDragItem(dragItem);
      return dragItem;
    },
    collect: (monitor: DragSourceMonitor<unknown, unknown>) => ({
      isDragging: monitor.isDragging(),
      handlerId: monitor.getHandlerId()
    }),
    end: (_draggedItem, monitor) => {
      setCurrentDragItem(null);
      const item = monitor.getItem();

      item.post && monitor.didDrop() ? endMove(item.post, item.index) : cancelMove();
    }
  });
  const { isTemporallySuspended } = useSubscriptionStatus();
  const postMenu: Array<ContainerListItem | ContainerListItemExtended> = useMemo(() => {
    if (!post || post.status === PostStatus.PUBLISHED) return [];
    const editPost = () => {
      if (isTemporallySuspended) {
        setSuspendedModalOpen(true);
        return;
      }
      setStickyMenu(false);

      setPostEditorContext({
        type: PostEditorContextEnum.edit,
        postId: post.id,
        onClose: success => {
          if (success) {
            props.reFetchPosts(post.socialProfile.id);
          }
        }
      });
    };

    return [
      {
        label: 'Preview Post',
        action: () => setShowPostPreview(true)
      },
      {
        label: 'Edit',
        action: editPost
      },
      {
        label: 'Trash',
        action: () => {
          setStickyMenu(false);
          props.trashSinglePost(post.id);
        },
        ...(showOnboarding && disableDelete && post.postFragment?.onboardingContent
          ? { disabled: true, disabledTooltipText: 'You’ll be able to delete these demo posts later.' }
          : {})
      }
    ];
  }, [showOnboarding, disableDelete, post, props, setPostEditorContext, setSuspendedModalOpen, isTemporallySuspended]);

  useEffect(() => {
    preview(getEmptyImage(), { captureDraggingState: false });
  }, [preview]);

  if (post && post.timing === PostTiming.QUEUE && post.status !== PostStatus.PUBLISHED) {
    drag(drop(ref));
  }

  const debouncedMovePostFunction = useMemo(
    () =>
      debounce(() => {
        if (currentDragItem) {
          movePost(currentDragItem.post.id, currentDragItem.index);
        }
      }, 200),
    [currentDragItem, movePost]
  );

  const callDebouncedMovePost = useCallback(() => {
    debouncedMovePostFunction();
  }, [debouncedMovePostFunction]);

  useEffect(() => {
    if (isDragging) {
      callDebouncedMovePost();
    }

    return () => {
      debouncedMovePostFunction.cancel(); // Assuming the debounce function has a cancel method.
    };
  }, [isDragging, callDebouncedMovePost, debouncedMovePostFunction]);

  const rightTopIcon = useMemo(
    () =>
      isCarousel ? (
        <div className={classes.GridIconWrapper}>
          <Icon name={IconsEnum.carousel} size={'large'} />
        </div>
      ) : hasVideo ? (
        <div className={classes.GridIconWrapper}>
          <Icon name={IconsEnum.video} size={'large'} />
        </div>
      ) : undefined,
    [hasVideo, isCarousel]
  );

  const getToolTipLabel = useCallback(
    (timing: PostTiming, publishedDate?: TDateTimeString) => {
      if (publishedDate) {
        return `Post published on ${formatDateString(publishedDate)}`;
      }

      switch (timing) {
        case PostTiming.QUEUE:
          return 'This Post is Queued using the Posting Plan';
        case PostTiming.SCHEDULE:
        case PostTiming.RECURRING:
        case PostTiming.NOW:
          return 'This Post is manually scheduled for a specific Date & Time';
        default:
          return timing;
      }
    },
    [formatDateString]
  );

  const leftBottomIcon = useMemo(() => {
    if (post && post.status !== PostStatus.PUBLISHED) {
      const icon = post.timing === PostTiming.QUEUE ? IconsEnum.queued : IconsEnum.pin;

      return (
        <div id={`PostTimeIcon-${post.id}`}>
          <Tooltip type={'regular'} target={`#PostTimeIcon-${post.id}`} pointer={true} position={'bottom'}>
            <>{getToolTipLabel(post.timing, post.datePosted ?? undefined)}</>
          </Tooltip>
          <div className={classes.GridLayerButtonWrapper}>
            <Icon name={icon} size={'small'} />
          </div>
        </div>
      );
    }
    return undefined;
  }, [getToolTipLabel, post]);

  const cardClasses = classNames(classes.GridMediaCard, {
    [classes.GridMediaCardDragging]: isDragging || isGrabbed,
    [classes.GridMediaCardInactive]:
      activeCategoryId && (post?.category?.id !== activeCategoryId || post.status === PostStatus.PUBLISHED),
    [classes.GridMediaCardInactiveSibling]:
      activeCategoryId &&
      post?.category?.id === activeCategoryId &&
      post?.timing !== PostTiming.QUEUE &&
      post.status !== PostStatus.PUBLISHED,
    [classes.GridMediaCardPastPost]: post?.status === PostStatus.PUBLISHED,
    [classes.GridMediaCardMobile]: isMobile,
    [classes.GridMediaCardMobileScrollAdjustment]: isMobile && hasScrolling,
    [classes.GridMediaCardScrollAdjustment]: !isMobile && hasScrolling,
    [classes.GridMediaCardScheduled]: post?.timing === PostTiming.SCHEDULE,
    [classes.GridMediaCardPlaceholder]: isPlaceholder,
    [classes.GridMediaCardNotGrabbable]: post?.timing !== PostTiming.QUEUE || post?.status === PostStatus.PUBLISHED
  });

  const imageWrapperClasses = classNames(classes.ImageWrapper, {
    [classes.ImageWrapperDisabled]: activeCategoryId && post?.category?.id !== activeCategoryId && !isPlaceholder
  });

  const categoryBlockClasses = classNames(classes.CategoryBlock, classes[`CategoryBlockColor${post?.category?.color}`]);
  const leftTopElement =
    activeCategoryId && post?.timing === PostTiming.QUEUE && !isPlaceholder && post.status !== PostStatus.PUBLISHED ? (
      <div className={categoryBlockClasses}></div>
    ) : undefined;
  const rightBottomElement =
    post?.status !== PostStatus.PUBLISHED ? (
      <ButtonMenu
        isSquareButton={false}
        variant={'solid'}
        color={ButtonColorsEnum.white}
        size={'small'}
        icon={IconsEnum.threeDot}
        buttonType={'transparent'}
        side={'right'}
        listItems={postMenu}
        closeOnMouseOut={false}
        onMenuShowEventCallback={setStickyMenu}
      />
    ) : undefined;

  const stickyPositions = [
    ElementOverlayPositions.BottomLeft,
    ...(leftTopElement ? [ElementOverlayPositions.TopLeft] : []),
    ...(rightTopIcon ? [ElementOverlayPositions.TopRight] : []),
    ...(stickyMenu ? [ElementOverlayPositions.BottomRight] : [])
  ];

  const firstValidThumbnailUrl = getFirstValidThumbnailUrl(post);

  return (
    <>
      <div ref={ref} data-handler-id={handlerId} className={cardClasses}>
        <Debugger tooltipContent={`Post ID: ${post?.id}`} modalContent={post} />
        {isPlaceholder ? (
          <div className={classes.PlaceHolderImage}>
            <Icon name={IconsEnum.photo} size={'large'} />
          </div>
        ) : (
          <>
            {!isDragging && (
              <ElementOverlay
                size={ElementOverlaySize.SMALL}
                cursor={
                  post?.timing !== PostTiming.QUEUE || post?.status === PostStatus.PUBLISHED
                    ? ElementOverlayCursor.default
                    : ElementOverlayCursor.move
                }
                stickyPositions={stickyPositions}
                defaultAction={() => {}}
                topLeft={leftTopElement}
                topRight={rightTopIcon}
                bottomLeft={leftBottomIcon}
                bottomRight={rightBottomElement}>
                <div className={imageWrapperClasses}>
                  {firstValidThumbnailUrl ? (
                    <img src={firstValidThumbnailUrl} alt={''} />
                  ) : (
                    <div className={classes.NoImage}>
                      <LinkPreviewNoImage />
                    </div>
                  )}
                </div>
              </ElementOverlay>
            )}
          </>
        )}
      </div>
      {showPostPreview && post?.postFragment ? (
        <PostPreviewModal
          post={post.postFragment}
          editPost={() => {
            if (isTemporallySuspended) {
              setSuspendedModalOpen(true);
              return;
            }
            setStickyMenu(false);
            setPostEditorContext({
              type: PostEditorContextEnum.edit,
              postId: post.id,
              onClose: success => {
                if (success) {
                  props.reFetchPosts(post.socialProfile.id);
                }
              }
            });
            setShowPostPreview(false);
          }}
          onClose={() => setShowPostPreview(false)}
        />
      ) : null}
    </>
  );
};
